import React, { useState } from "react";
import {
  Button,
  LinearProgress,
  Typography,
  Box,
  Paper,
  Stack,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

export function FileUpload3() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | success | error

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    console.log("📁 File selected:", selected);
    setFile(selected);
    setMessage("");
    setProgress(0);
    setStatus("idle");
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage("⚠️ Please select a file first.");
      return;
    }

    setUploading(true);
    setMessage("Requesting signed URL...");
    setStatus("idle");

    try {
      console.log("🔹 Step 1: Requesting signed URL from Cloud Function...");
      const res = await fetch("https://us-central1-northern-gasket-413415.cloudfunctions.net/get-upload-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
        }),
      });

      console.log("🔹 Cloud Function response status:", res.status);
      const data = await res.json();
      console.log("🔹 Signed URL response JSON:", data);

      if (!data.url) throw new Error("No signed URL returned from Cloud Function");

      const signedUrl = data.url;
      console.log("✅ Received signed URL:", signedUrl);

      console.log("🔹 Step 2: Uploading file to GCS via signed URL...");

      const xhr = new XMLHttpRequest();
      xhr.open("PUT", signedUrl, true);
      xhr.setRequestHeader("Content-Type", file.type);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          console.log(`📈 Upload progress: ${percent}%`);
          setProgress(percent);
        }
      };

      xhr.onload = () => {
        console.log("✅ Upload completed. HTTP status:", xhr.status);
        if (xhr.status === 200 || xhr.status === 204) {
          setStatus("success");
          setMessage("✅ File uploaded successfully!");
        } else {
          console.error("❌ Upload failed. Server response:", xhr.responseText);
          setStatus("error");
          setMessage(`❌ Upload failed with status ${xhr.status}`);
        }
        setUploading(false);
      };

      xhr.onerror = () => {
        console.error("❌ Network error during upload:", xhr.statusText);
        setStatus("error");
        setMessage("❌ Network error during upload");
        setUploading(false);
      };

      xhr.send(file);
      console.log("🚀 Upload started...");
    } catch (error) {
      console.error("❌ Error in upload flow:", error);
      setStatus("error");
      setMessage(`❌ ${error.message}`);
      setUploading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
      <Paper
        elevation={4}
        sx={{
          p: 4,
          width: 420,
          borderRadius: 3,
          textAlign: "center",
          bgcolor: "#fafafa",
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Upload File to Google Cloud Storage
        </Typography>

        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Select a PDF or image file to upload securely via signed URL.
        </Typography>

        <Stack spacing={2} alignItems="center">
          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUploadIcon />}
            color="primary"
            sx={{
              textTransform: "none",
              px: 3,
              py: 1.2,
              borderRadius: 2,
            }}
            disabled={uploading}
          >
            {file ? "Change File" : "Choose File"}
            <input type="file" hidden onChange={handleFileChange} />
          </Button>

          {file && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Selected: <strong>{file.name}</strong>
            </Typography>
          )}

          <Button
            variant="contained"
            color="success"
            onClick={handleUpload}
            disabled={!file || uploading}
            sx={{
              width: "60%",
              py: 1.2,
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            {uploading ? (
              <>
                <CircularProgress size={22} sx={{ mr: 1 }} /> Uploading...
              </>
            ) : (
              "Upload"
            )}
          </Button>

          {uploading && (
            <Box sx={{ width: "100%", mt: 2 }}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                {progress}% completed
              </Typography>
            </Box>
          )}

          {message && (
            <Box sx={{ mt: 2 }}>
              {status === "success" && (
                <CheckCircleIcon color="success" sx={{ verticalAlign: "middle", mr: 1 }} />
              )}
              {status === "error" && (
                <ErrorIcon color="error" sx={{ verticalAlign: "middle", mr: 1 }} />
              )}
              <Typography
                variant="body1"
                color={status === "error" ? "error" : "success.main"}
                sx={{ display: "inline" }}
              >
                {message}
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}


